import React from "react";
import "./ParticipantsMobile.css";
import { RedArrowSVG } from "../../../constants/svgs";
import { useI18n } from "../../../i18n/i18n";
import { dict } from "../../../i18n/dict";

import main_characters from "../../../assets/images/webp/main_characters.webp";
import alvaro from "../../../assets/images/webp/characters/mobile/Alvaro.webp";
import cristina from "../../../assets/images/webp/characters/mobile/Christina.webp";
import diana from "../../../assets/images/webp/characters/mobile/Diana.webp";
import jaime from "../../../assets/images/webp/characters/mobile/Jaime.webp";
import lucas from "../../../assets/images/webp/characters/mobile/Lucas.webp";
import manuel from "../../../assets/images/webp/characters/mobile/Manuel.webp";
import octavio from "../../../assets/images/webp/characters/mobile/Octavio.webp";
import ximena from "../../../assets/images/webp/characters/mobile/Ximena.webp";

type MemberId = (typeof dict.home.participants.members)[number]["id"];

const ParticipantsMobile: React.FC = () => {
    const { lang } = useI18n();

    const members = [
        { id: 1 as MemberId, infoX: 16, focusX: 16, overlay: lucas },
        { id: 2 as MemberId, infoX: 24, focusX: 24, overlay: alvaro },
        { id: 3 as MemberId, infoX: 36, focusX: 36, overlay: ximena },
        { id: 4 as MemberId, infoX: 46, focusX: 46, overlay: jaime },
        { id: 5 as MemberId, infoX: 56, focusX: 56, overlay: manuel },
        { id: 6 as MemberId, infoX: 62, focusX: 62, overlay: octavio },
        { id: 7 as MemberId, infoX: 76, focusX: 76, overlay: cristina },
        { id: 8 as MemberId, infoX: 82, focusX: 82, overlay: diana },
    ] as const;

    const getMemberCopy = (id: MemberId) =>
        dict.home.participants.members.find((m) => m.id === id) ?? null;

    const [index, setIndex] = React.useState(0);

    const viewportRef = React.useRef<HTMLDivElement | null>(null);
    const containerWidthRef = React.useRef(0);
    const touchStartXRef = React.useRef<number | null>(null);
    const touchStartYRef = React.useRef<number | null>(null);

    const [dragX, setDragX] = React.useState(0);
    const [isDragging, setIsDragging] = React.useState(false);

    const SWIPE_THRESHOLD_RATIO = 0.2; // 20% of width

    const goNext = () => setIndex((prev) => (prev === members.length - 1 ? prev : prev + 1));
    const goPrev = () => setIndex((prev) => (prev === 0 ? prev : prev - 1));

    const handlePrevClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        goPrev();
    };

    const handleNextClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        goNext();
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        touchStartXRef.current = touch.clientX;
        touchStartYRef.current = touch.clientY;
        setIsDragging(true);
        if (viewportRef.current) containerWidthRef.current = viewportRef.current.clientWidth;
        setDragX(0);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (touchStartXRef.current == null || touchStartYRef.current == null) return;
        const touch = e.touches[0];
        const diffX = touch.clientX - touchStartXRef.current;
        const diffY = touch.clientY - touchStartYRef.current;

        if (Math.abs(diffY) > Math.abs(diffX)) {
            setIsDragging(false);
            setDragX(0);
            return;
        }

        setDragX(diffX);
    };

    const handleTouchEnd = () => {
        if (!containerWidthRef.current) {
            setIsDragging(false);
            setDragX(0);
            return;
        }

        const threshold = containerWidthRef.current * SWIPE_THRESHOLD_RATIO;

        if (Math.abs(dragX) > threshold) {
            if (dragX < 0) goNext();
            else goPrev();
        }

        setIsDragging(false);
        setDragX(0);
        touchStartXRef.current = null;
        touchStartYRef.current = null;
    };

    const baseTranslate = -100 * index;
    const dragPercent =
        containerWidthRef.current > 0 ? (dragX / containerWidthRef.current) * 100 : 0;
    const trackTranslate = baseTranslate + dragPercent;

    return (
        <div className="scene-mobile">
            <div
                ref={viewportRef}
                className="scene-mobile-viewport"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div
                    className="scene-mobile-track"
                    style={{
                        transform: `translateX(${trackTranslate}%)`,
                        transition: isDragging ? "none" : "transform 0.4s ease",
                    }}
                >
                    {members.map((m) => {
                        const copy = getMemberCopy(m.id);
                        const name = copy?.name?.[lang] ?? "";
                        const description = copy?.description?.[lang] ?? "";

                        return (
                            <div key={m.id} className="scene-mobile-slide">
                                <div className="scene-mobile-photo-wrapper">
                                    <img
                                        className="scene-mobile-base"
                                        src={main_characters}
                                        alt=""
                                        style={{ objectPosition: `${m.focusX}% 50%` }}
                                    />
                                    <img
                                        className="scene-mobile-overlay"
                                        src={m.overlay}
                                        alt={name}
                                        style={{ objectPosition: `${m.focusX}% 50%` }}
                                    />

                                    <div className="scene-mobile-info">
                                        <h2 className="scene-mobile-info-name">{name}</h2>
                                        <p className="scene-mobile-info-description">{description}</p>

                                        <div
                                            className="scene-mobile-info-arrow"
                                            style={{ "--arrow-x": `${m.infoX}%` } as React.CSSProperties}
                                        >
                                            <RedArrowSVG />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <button
                    type="button"
                    className="scene-mobile-nav scene-mobile-nav-left"
                    onClick={handlePrevClick}
                    disabled={index === 0}
                    aria-label={lang === "eng" ? "Previous" : "Anterior"}
                >
                    <svg width="16" height="29" viewBox="0 0 16 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14.8492 28.6379L0.707107 14.4957L14.8492 0.353592" stroke="white" />
                    </svg>
                </button>

                <button
                    type="button"
                    className="scene-mobile-nav scene-mobile-nav-right"
                    onClick={handleNextClick}
                    disabled={index === members.length - 1}
                    aria-label={lang === "eng" ? "Next" : "Siguiente"}
                >
                    <svg width="16" height="29" viewBox="0 0 16 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.353561 0.353592L14.4957 14.4957L0.353561 28.6379" stroke="white" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default ParticipantsMobile;
